import {compact} from 'lodash';
import {useEffect, useState} from 'react';
import {
    DEFAULT_BOARD_SIZE,
    extractBoardSize,
    getRoot,
    Ki,
    Sgf,
    TNode,
} from 'ghostban';

import {
    getFirstToMoveColorFromRoot,
    convertSgfPathToCompressedPath,
    useDispatch,
    useSelector,
    getFirstSetupNodeFromRoot,
} from '@utils';
import {boardSlice} from '@reducers';

export const useSgfTree = (options: {allowCustomProps?: boolean} = {}) => {
    const {allowCustomProps = false} = options;
    const [currentNode, setCurrentNode] = useState<TNode>();
    const dispatch = useDispatch();
    const {
        root,
        sgf,
        update,
        currentNodeId,
        currentNode: currentNodeProp,
        path,
        compressedPath,
    } = useSelector(s => s.board);
    const [boardSize, setBoardSize] = useState(DEFAULT_BOARD_SIZE);
    const [firstToMove, setFirstToMove] = useState(Ki.Empty);
    const [firstSetupNode, setFirstSetupNode] = useState<TNode | null>(null);

    useEffect(() => {
        if (!sgf) return;
        const parser = new Sgf(sgf, {allowCustomProps});
        const root = parser.root;
        dispatch(boardSlice.actions.setRoot(parser.root));
        setFirstToMove(getFirstToMoveColorFromRoot(root));
        // TODO: This is a hack to force update the board, will effect performance
        // dispatch(boardSlice.actions.forceUpdate());
        const firstSetupNode = getFirstSetupNodeFromRoot(root);
        if (firstSetupNode) {
            setFirstSetupNode(firstSetupNode);
        }
        if (root) {
            setBoardSize(extractBoardSize(root));
        }
    }, [dispatch, sgf, allowCustomProps]);

    useEffect(() => {
        if (!root) return;

        const currentNode = root.first(n => n.model.id === currentNodeId);
        if (currentNode) {
            // TODO: Use setCurrentNode instead of dispatching to avoid circular rendering
            setCurrentNode(currentNode);
            const path = compact(
                currentNode.getPath().map(n => n.model.moveProps[0]?.toString())
            ).join(';');
            dispatch(boardSlice.actions.setPath(path));
            dispatch(
                boardSlice.actions.setCompressedPath(
                    convertSgfPathToCompressedPath(path)
                )
            );
        }
    }, [currentNodeId, currentNodeProp, root, update, dispatch]);

    useEffect(() => {
        if (!currentNodeProp) return;
        const root = getRoot(currentNodeProp);
        dispatch(boardSlice.actions.setSgf(new Sgf(root).toSgf()));
        dispatch(boardSlice.actions.setRoot(root));
        dispatch(
            boardSlice.actions.setCurrentNodeId(currentNodeProp?.model.id)
        );
    }, [currentNodeProp, dispatch]);

    return {
        currentNodeId,
        currentNode,
        root,
        boardSize,
        update,
        firstToMove,
        sgf,
        path,
        compressedPath,
        firstSetupNode,
    };
};
